import { HttpApiService } from "@/shared/services/httpApi.service";

import { CARDS_API_ENDPOINTS } from "../constants/cards.constants";
import type { CardCopTopUp, CardCopTopUpTerms, CreateCardCopTopUpPayload } from "../types/cardCopTopUp.types";

const topUpsUrl = (cardId: string) => `${CARDS_API_ENDPOINTS.CARDS}/${cardId}/cop-top-ups`;

// A second click must not reach the backend's idempotency lock, which answers a concurrent duplicate with a 409.
const inFlightCreations = new Map<string, Promise<CardCopTopUp>>();

export const CardCopTopUpsService = {
  async terms(cardId: string): Promise<CardCopTopUpTerms> {
    const { data } = await HttpApiService.get<CardCopTopUpTerms>(`${topUpsUrl(cardId)}/terms`);
    return data;
  },

  async get(cardId: string, topUpId: string): Promise<CardCopTopUp> {
    const { data } = await HttpApiService.get<CardCopTopUp>(`${topUpsUrl(cardId)}/${topUpId}`);
    return data;
  },

  async findByKey(cardId: string, payload: CreateCardCopTopUpPayload): Promise<CardCopTopUp> {
    const { data } = await HttpApiService.get<CardCopTopUp>(
      `${topUpsUrl(cardId)}/by-key/${encodeURIComponent(payload.idempotency_key)}`,
      { params: { quote_id: payload.quote_id } },
    );
    return data;
  },

  createOnce(
    cardId: string,
    payload: CreateCardCopTopUpPayload,
    twoFactorCode: string,
  ): Promise<CardCopTopUp> {
    const running = inFlightCreations.get(payload.idempotency_key);
    if (running) return running;
    const request = HttpApiService.post<CardCopTopUp>(topUpsUrl(cardId), payload, {
      headers: {
        "Idempotency-Key": payload.idempotency_key,
        "x-two-factor-code": twoFactorCode,
      },
    })
      .then(({ data }) => data)
      .finally(() => inFlightCreations.delete(payload.idempotency_key));
    inFlightCreations.set(payload.idempotency_key, request);
    return request;
  },
};
